import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';
import { showError, showSuccess } from '../utils/errorHandler';

function AdminApprovedCancellations() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchApprovedCancellations();
  }, []);

  const fetchApprovedCancellations = async () => {
    try {
      const response = await api.get('/api/loans');
      // Filter loans with APPROVED cancellation requests (not executed yet)
      const approvedCancellations = response.data.filter(loan => 
        loan.cancellation_request && loan.cancellation_request.status === 'approved'
      );
      
      // Fetch lender names
      const loansWithNames = await Promise.all(
        approvedCancellations.map(async (loan) => {
          if (!loan.lender_name && loan.lender_id) {
            try {
              const lenderRes = await api.get(`/api/admin/lenders`);
              const lender = lenderRes.data.find(l => l.user._id === loan.lender_id);
              if (lender) {
                loan.lender_name = `${lender.user.name} ${lender.user.surname}`;
              }
            } catch (err) {
              loan.lender_name = 'Prestamista';
            }
          }
          return loan;
        })
      );
      
      setLoans(loansWithNames);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteCancellation = async (loanId, lenderName) => {
    if (!confirm(t('cancellations.confirmExecute', { name: lenderName }))) {
      return;
    }

    setExecuting(loanId);
    try {
      await api.post(`/api/admin/loans/${loanId}/execute-cancellation`, {
        cancellation_date: new Date().toISOString()
      });
      showSuccess(t('cancellations.executed'));
      fetchApprovedCancellations();
    } catch (error) {
      showError(error, t('cancellations.errorExecute'));
    } finally {
      setExecuting(null);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={t('cancellations.approvedTitle')} />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {loans.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '4rem', 
              background: 'rgba(255,255,255,0.03)', 
              borderRadius: '20px' 
            }}>
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</p>
              <h3 style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.9)' }}>
                No hi ha cancel·lacions aprovades pendents d'executar
              </h3>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {loans.map((loan) => {
                const request = loan.cancellation_request;
                return (
                  <div 
                    key={loan._id} 
                    className="dark-card" 
                    style={{ 
                      padding: '2rem',
                      border: '1px solid rgba(16,185,129,0.4)',
                      background: 'rgba(16,185,129,0.08)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                      <div>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white' }}>
                          {loan.lender_name || 'Prestamista'}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
                          Aprovada: {new Date(request.approved_at).toLocaleDateString('ca-ES', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <span style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(16,185,129,0.25)',
                        color: '#10b981',
                        borderRadius: '20px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        height: 'fit-content'
                      }}>
                        ✅ Aprovada
                      </span>
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '1.5rem', 
                      marginBottom: '1.5rem' 
                    }}>
                      <div style={{ padding: '1.5rem', background: 'rgba(59,130,246,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          CAPITAL
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#60a5fa' }}>
                          {loan.capital.toLocaleString('es-ES')}€
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(16,185,129,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          INTERÈS ORIGINAL
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#34d399' }}>
                          {loan.interest_rate}%
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(239,68,68,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          INTERÈS AMB PENALITZACIÓ
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                          {(loan.interest_rate - 2.5).toFixed(1)}%
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(249,115,22,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          DIES RESTANTS
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#fb923c' }}>
                          {loan.days_remaining || 0}
                        </p>
                      </div>
                    </div>

                    <div style={{ 
                      padding: '1rem', 
                      background: 'rgba(16,185,129,0.1)', 
                      borderRadius: '8px', 
                      marginBottom: '1.5rem',
                      borderLeft: '3px solid #10b981'
                    }}>
                      <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', fontWeight: '600' }}>
                        {t('cancellations.pendingExecution')}
                      </p>
                      <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
                        Aquesta cancel·lació ha estat aprovada però NO executada. El préstec continua actiu i acumulant interessos. Executa-la quan sigui possible.
                      </p>
                    </div>

                    <button
                      onClick={() => handleExecuteCancellation(loan._id, loan.lender_name)}
                      disabled={executing === loan._id}
                      style={{ 
                        width: '100%',
                        padding: '1rem', 
                        background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '12px', 
                        fontWeight: '600', 
                        cursor: executing === loan._id ? 'not-allowed' : 'pointer',
                        opacity: executing === loan._id ? 0.5 : 1,
                        fontSize: '1rem'
                      }}
                    >
                      {executing === loan._id ? t('cancellations.executing') : t('cancellations.executeNow')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminApprovedCancellations;
